//
// Shared error copy for team creation, hashtag invitations and coach invites.
//
// The repo idiom is a local errorMessage(err) per page, and for a one-off
// that's right. These codes are not one-offs: the affiliation 409 set is
// consumed by two league surfaces, and the tag rules by three (league create,
// league affiliate, coach create). This codebase has a documented history of
// copy drifting across duplicated sites, so they live in one place.
//
// No client-side hashtag validation beyond a normalize, on purpose. The
// server's tagCheck is a 60-line frozen gate copied verbatim from the checkout
// Lambda; a fourth copy in JS would drift out of sync with the three that
// already exist. The server is authoritative and `err.detail` carries the
// reason it rejected.
//

/** Trim, strip a leading '#', lowercase — matches iOS's normalize exactly. */
export function normalizeTag(raw) {
  return String(raw || "").trim().replace(/^#/, "").toLowerCase();
}

const GENERIC = "Something went wrong. Please try again.";

const TAG_RULE = "Hashtags are 3-25 characters — lowercase letters, numbers and _, starting with a letter.";

/**
 * POST/DELETE /league/affiliations.
 *
 * NOTE `invitePending` is NOT routed here: it is a SUCCESS. The server found
 * an unanswered invitation and re-notified the coach rather than duplicating
 * the row, and only returns 409 because there was nothing new to create.
 * Callers must special-case it before calling this.
 */
export function affiliationErrorCopy(err) {
  switch (err?.code) {
    case "tagRequired":
      return "Enter the team's hashtag.";
    case "invalidTag":
      return err.detail || TAG_RULE;
    case "tagNotFound":
      return "No team with that hashtag exists on polyp.me.";
    case "alreadyInLeague":
      return "That team is already one of your own.";
    case "alreadyAffiliated":
      return "That team is already in your league.";
    case "teamHasNoCoach":
      return "That team doesn't have a coach yet, so nobody can accept. Invite the coach by phone from the Coaches tab instead.";
    case "affiliationNotFound":
      return "That invitation is no longer there.";
    case "teamHasScheduledGames":
      return "This team has games on your schedule. Remove or reschedule them first.";
    default:
      return err?.message || GENERIC;
  }
}

/** POST /league/teams and POST /coach/teams. */
export function teamTagErrorCopy(err) {
  switch (err?.code) {
    case "nameRequired":
      return "Enter a team name.";
    case "tagRequired":
      return "Pick a hashtag — it's how leagues find your team.";
    case "invalidTag":
      return err.detail || TAG_RULE;
    case "tagTaken":
      return "That hashtag is taken — try another.";
    case "coachPhoneInvalid":
      return "Enter a valid phone number for the coach.";
    default:
      return err?.message || GENERIC;
  }
}

/** POST /league/invites. */
export function coachInviteErrorCopy(err) {
  switch (err?.code) {
    case "phoneNumberInvalid":
    case "phoneNumberRequired":
      return "Enter a valid phone number.";
    case "phoneHasIncompatibleAccount":
      return err.existingAccountType
        ? `That number already belongs to a ${err.existingAccountType} account.`
        : "That number already belongs to a different kind of account.";
    default:
      return err?.message || GENERIC;
  }
}

/** PUT/DELETE /coach/affiliations/{leagueId}. */
export function coachAffiliationErrorCopy(err) {
  switch (err?.code) {
    case "inviteNotFound":
      return "That invitation is no longer available.";
    case "leagueIdRequired":
      return "Couldn't identify that league.";
    default:
      return err?.message || GENERIC;
  }
}
